// Vendor Connection v1 — the buyer "My Vendors" tab: every vendor Phoenix
// has connected to this buyer's loan(s), with that vendor's goals on the
// loan, their monthly report cards (§1/§2 — the server never sends more),
// the buyer's own star ratings, and a private notes area the vendor never
// sees. Own-loan data only; expanding a new card stamps it seen (clears the
// nav badge).

import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Handshake } from "lucide-react";
import {
  Badge,
  EmptyState,
  SkeletonList,
  StatusBadge,
  SubmitButton,
  SubmittingHint,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import {
  ENGAGEMENT_STATUS_LABEL,
  portalBuyerConnectionsApi,
  type BorrowerConnectedVendorDto,
  type BorrowerConnectedVendorLoanDto,
  type EngagementStatus,
} from "../api/connections";
import { formatReportMonth } from "../api/borrower";
import { BorrowerStarsDisplay } from "../components/BorrowerStarRating";
import { useNewConnectionBadge } from "../components/NewConnectionBadgeContext";

const ENGAGEMENT_TONE: Record<EngagementStatus, StatusTone> = {
  "new": "info",
  "contacted": "progress",
  "in-negotiation": "warn",
  "under-contract": "ok",
};

export function BorrowerVendorsTab() {
  const [vendors, setVendors] = useState<BorrowerConnectedVendorDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await portalBuyerConnectionsApi.list();
        if (!cancelled) setVendors(rows);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your vendors.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <p className="text-small text-status-error">{error}</p>;
  }
  if (vendors === null) {
    return <SkeletonList data-testid="borrower-vendors-loading" />;
  }
  if (vendors.length === 0) {
    return (
      <EmptyState
        variant="empty"
        icon={Handshake}
        title="No connected vendors yet"
        description="When Phoenix connects a vendor to your loan, they'll appear here with their goals, monthly report cards, and your ratings."
      />
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="borrower-vendors-list">
      {vendors.map((vendor) => (
        <article
          key={vendor.vendorId}
          className="rounded-lg border border-border bg-white-pure p-space-4"
          data-testid="borrower-vendor-card"
        >
          <header className="flex items-center gap-space-2 mb-space-3">
            <h2 className="font-display font-semibold text-h4 m-0">{vendor.vendorName}</h2>
            {vendor.vendorType && <Badge>{vendor.vendorType}</Badge>}
            {vendor.loans.some((l) => l.isNew) && (
              <span
                className="h-2 w-2 rounded-pill bg-gold"
                aria-label="New connection"
                data-testid="borrower-vendor-new-dot"
              />
            )}
          </header>
          <div className="flex flex-col gap-space-3">
            {vendor.loans.map((loan) => (
              <VendorLoanSection key={loan.connectionId} loan={loan} />
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}

function VendorLoanSection({ loan }: { loan: BorrowerConnectedVendorLoanDto }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(!loan.isNew);
  const [notes, setNotes] = useState(loan.notes ?? "");
  const [savedNotes, setSavedNotes] = useState(loan.notes ?? "");

  // First view stamps the server-side FirstViewedAt and refreshes the
  // sidebar badge (the chrome stays mounted across ?tab= navigation, so
  // without the refresh the count went stale until a full reload). The
  // card opens optimistically; a failed stamp is captured by the mutation
  // and just leaves the badge until the next view.
  const badge = useNewConnectionBadge();
  const markSeen = useSubmittingMutation(
    () => portalBuyerConnectionsApi.markViewed(loan.connectionId),
    { refresh: badge.refresh },
  );
  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !seen) {
      setSeen(true);
      void markSeen.submit();
    }
  };

  const saveNotes = useSubmittingMutation(
    async () => {
      const updated = await portalBuyerConnectionsApi.updateNotes(
        loan.connectionId,
        notes.trim() === "" ? null : notes,
      );
      setSavedNotes(updated.notes ?? "");
      setNotes(updated.notes ?? "");
      toast.success("Notes saved.");
    },
    {
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Couldn't save your notes."),
    },
  );
  const saving = saveNotes.pending;

  return (
    <section className="rounded-md border border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-space-2 p-space-3 text-left"
        data-testid="borrower-vendor-loan-toggle"
      >
        {open ? (
          <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
        ) : (
          <ChevronRight size={16} strokeWidth={1.5} aria-hidden />
        )}
        <span className="font-display font-semibold text-small">{loan.loanLabel}</span>
        <span className="text-caption text-gray-mid">{loan.roleOnLoan}</span>
        <StatusBadge tone={ENGAGEMENT_TONE[loan.engagementStatus]}>
          {ENGAGEMENT_STATUS_LABEL[loan.engagementStatus]}
        </StatusBadge>
        {!seen && (
          <span
            className="h-2 w-2 rounded-pill bg-gold"
            aria-label="New"
            data-testid="borrower-vendor-loan-new-dot"
          />
        )}
        <span className="ml-auto text-caption text-gray-mid">
          Connected <Timestamp value={loan.connectedAt} />
        </span>
      </button>

      {open && (
        <div className="px-space-3 pb-space-3 flex flex-col gap-space-3">
          <div>
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              Goals this vendor carries
            </h3>
            {loan.goals.length === 0 ? (
              <p className="text-small text-gray-mid m-0">
                No goals are assigned to this vendor yet.
              </p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
                {loan.goals.map((goal) => (
                  <li key={goal.id} className="text-small flex items-center gap-space-2">
                    <span>{goal.title}</span>
                    <Badge>{goal.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              Monthly report cards
            </h3>
            {loan.reports.length === 0 ? (
              <p className="text-small text-gray-mid m-0">No accepted reports yet.</p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
                {loan.reports.map((report) => (
                  <li
                    key={report.reportId}
                    className="rounded-md bg-white-warm p-space-2"
                    data-testid="borrower-vendor-report-card"
                  >
                    <div className="flex items-center gap-space-2">
                      <span className="font-display font-semibold text-small">
                        {formatReportMonth(report.periodStart)}
                      </span>
                      {report.signOff ? (
                        <BorrowerStarsDisplay value={report.signOff.overallRating} />
                      ) : (
                        <Badge>Awaiting your sign-off</Badge>
                      )}
                    </div>
                    {report.publicNarrative && (
                      <p className="text-small text-gray-mid m-0 mt-space-1 whitespace-pre-wrap">
                        {report.publicNarrative}
                      </p>
                    )}
                    {report.goalGrades.length > 0 && (
                      <ul className="m-0 mt-space-1 p-0 list-none flex flex-col gap-[2px]">
                        {report.goalGrades.map((grade) => (
                          <li key={grade.goalId} className="text-caption text-gray-mid">
                            {grade.goalTitle}: {grade.score}/100
                            {grade.isComplete ? " · complete" : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              My notes
            </h3>
            <p className="text-caption text-gray-mid m-0 mb-space-1">
              Private to you — the vendor never sees these.
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="borrower-vendor-notes"
            />
            <div className="mt-space-1 flex flex-col items-start gap-space-1">
              <SubmitButton
                variant="secondary"
                onClick={() => void saveNotes.submit()}
                pending={saving}
                pendingLabel="Saving…"
                disabled={notes === savedNotes}
                data-testid="borrower-vendor-notes-save"
              >
                Save notes
              </SubmitButton>
              <SubmittingHint pending={saving} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
